<template>
  <div class="streamer-detail">
    <!-- 页面标题 -->
    <div class="text-white py-4 px-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">LIVE Dashboard for creator</h1>
          <p class="text-gray-300 text-base mt-1">Real-time analytics and audience insights</p>
        </div>
      </div>
    </div>

    <!-- 主要内容区 -->
    <main class="max-w-7xl mx-auto px-6">

      <!-- 加载状态 -->
      <div v-if="loading" class="text-center py-12">
        <div class="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-lg text-gray-600">加载数据中...</p>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="text-center py-12">
        <i class="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
        <p class="text-lg text-gray-600 mb-4">{{ error }}</p>
        <button @click="loadStreamerData" class="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors">
          重试
        </button>
      </div>

      <!-- 主播详情内容 -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4 md:gap-6">
        <!-- 左侧边栏 - 主播信息和日程 -->
        <div class="xl:col-span-3 space-y-4 md:space-y-6">
          <!-- 主播资料卡片 -->
          <div class="glass-panel from-gray-800 to-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-700">
            <div class="text-center mb-6">
              <div class="relative inline-block">
                <img 
                  :src="streamerInfo.avatar || 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=60'" 
                  :alt="streamerInfo.name"
                  class="w-24 h-24 rounded-full mx-auto mb-3 border-3 border-purple-500 shadow-lg object-cover"
                >
                <!-- 直播状态指示器 - 闪烁绿色小圆点 -->
                <div 
                  v-if="isLive"
                  class="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-gray-800 flex items-center justify-center animate-pulse"
                >
                  <i class="fas fa-play text-white text-xs"></i>
                </div>
                <!-- 非直播状态显示静态绿色小圆点 -->
                <div 
                  v-else
                  class="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-gray-800 flex items-center justify-center"
                >
                  <i class="fas fa-play text-white text-xs"></i>
                </div>
              </div>
              <h2 class="text-2xl font-bold text-white mb-1">{{ streamerInfo.name }}</h2>
              <p class="text-gray-400 text-sm">@{{ streamerInfo.socialHandle || 'yuki_live_official' }}</p>
            </div>
            
            <!-- 粉丝数 -->
            <div class="bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-xl p-4 mb-4 border border-purple-500/30">
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <div class="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center mr-3">
                    <i class="fas fa-users text-purple-400"></i>
                  </div>
                  <span class="text-gray-300 text-sm font-medium">Followers</span>
                </div>
                <span class="text-white font-bold text-xl">{{ formatNumber(streamerInfo.followers) }}</span>
              </div>
            </div>
          </div>

          <!-- 日程安排 -->
          <div class="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-700">
            <h3 class="text-white font-bold text-xl mb-2">Schedule</h3>
            <p class="text-gray-400 text-sm mb-6">{{ liveSessionData.schedule || 'Mon-Fri, 19:00-21:00 JST' }}</p>
            
              <div class="space-y-4">
                <div 
                  v-for="(session, index) in liveSessionData.sessions" 
                  :key="session.id || session.sessionId || index"
                  @click="selectSession(session)"
                  :class="[
                    'session-card bg-gradient-to-r rounded-xl p-4 border transition-all duration-300 cursor-pointer',
                    selectedSessionId === session.sessionId 
                      ? 'selected from-purple-600/30 to-blue-600/30 border-purple-500/60 shadow-lg shadow-purple-500/20' 
                      : 'from-purple-600/10 to-blue-600/10 border-purple-500/20 hover:border-purple-500/40'
                  ]"
                >
                  <div class="flex items-center justify-between mb-3">
                    <span class="text-white text-sm font-semibold">{{ session.title || '直播时段' }}</span>
                    <div class="flex items-center space-x-2">
                      <div class="w-6 h-6 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <i class="fas fa-chart-line text-purple-400 text-xs"></i>
                      </div>
                      <div v-if="selectedSessionId === session.sessionId" class="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    </div>
                  </div>
                  <p class="text-gray-400 text-xs mb-3">{{ formatSessionTime(session.sessionId) || '19:00-21:00' }}</p>
                  <div class="flex justify-between text-sm">
                    <span class="text-white font-medium">{{ formatNumber(session.onlineUsers || 0) }} viewers</span>
                    <span class="text-white font-medium">{{ formatNumber(session.likeCount || 0) }} likes</span>
                    <span class="text-yellow-400 font-bold flex items-center">
                      <i class="fas fa-coins mr-1 text-sm"></i>
                      {{ formatGiftValue(session.totalGiftValue || 0) }}
                    </span>
                  </div>
                </div>
              
              <!-- 默认显示内容（当没有数据时） -->
              <div v-if="liveSessionData.sessions.length === 0" class="space-y-4">
                <div 
                  class="bg-gradient-to-r from-purple-600/10 to-blue-600/10 rounded-xl p-4 border border-purple-500/20 hover:border-purple-500/40 transition-all duration-300"
                  @click="selectSession({ sessionId: 'default-session-1' })"
                >
                  <div class="flex items-center justify-between mb-3">
                    <span class="text-white text-sm font-semibold">晚间舞蹈时段</span>
                    <div class="w-6 h-6 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <i class="fas fa-chart-line text-purple-400 text-xs"></i>
                    </div>
                  </div>
                  <p class="text-gray-400 text-xs mb-3">18:00-20:30</p>
                  <div class="flex justify-between text-sm">
                    <span class="text-white font-medium">{{ formatNumber(12800) }} viewers</span>
                    <span class="text-yellow-400 font-bold flex items-center">
                      <i class="fas fa-coins mr-1 text-sm"></i>
                      {{ formatNumber(8600) }}
                    </span>
                  </div>
                </div>
                
                <div 
                  class="bg-gradient-to-r from-purple-600/10 to-blue-600/10 rounded-xl p-4 border border-purple-500/20 hover:border-purple-500/40 transition-all duration-300"
                  @click="selectSession({ sessionId: 'default-session-2' })"
                >
                  <div class="flex items-center justify-between mb-3">
                    <span class="text-white text-sm font-semibold">夜间直播秀</span>
                    <div class="w-6 h-6 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <i class="fas fa-chart-line text-purple-400 text-xs"></i>
                    </div>
                  </div>
                  <p class="text-gray-400 text-xs mb-3">19:00</p>
                  <div class="flex justify-between text-sm">
                    <span class="text-white font-medium">{{ formatNumber(8400) }} viewers</span>
                    <span class="text-yellow-400 font-bold flex items-center">
                      <i class="fas fa-coins mr-1 text-sm"></i>
                      {{ formatNumber(5300) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
          </div>

        </div>

        <!-- 主内容区 - 参与度时间线和AI洞察 -->
        <div class="md:col-span-2 xl:col-span-6 space-y-4 md:space-y-6">
          <!-- 参与度时间线 -->
          <div class="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-700">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-white font-bold text-2xl">Engagement Timeline</h3>
              <div class="flex space-x-2">
                <button 
                  v-for="tab in timelineTabs" 
                  :key="tab.value"
                  @click="selectedTimelineTab = tab.value"
                  :class="[
                    'px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300',
                    selectedTimelineTab === tab.value 
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                  ]"
                >
                  {{ tab.label }}
                </button>
              </div>
            </div>
            
            <!-- 指标选择 -->
            <div class="flex flex-wrap gap-4 mb-6">
              <label v-for="metric in metrics" :key="metric.value" class="flex items-center cursor-pointer group">
                <input 
                  type="radio" 
                  :value="metric.value" 
                  v-model="selectedMetric"
                  class="mr-3 text-purple-600 focus:ring-purple-500 focus:ring-2"
                >
                <span class="text-gray-300 text-sm group-hover:text-white transition-colors">{{ metric.label }}</span>
              </label>
            </div>

            <!-- PK战斗状态 -->
            <div v-if="false" class="space-y-3 mb-6">
              <div class="flex items-center space-x-4 bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-xl p-4 border border-green-500/30">
                <div class="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                  PK対戦
                </div>
                <div class="flex items-center space-x-3">
                  <img src="https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=40&q=60" 
                       alt="Sakura Stream" class="w-8 h-8 rounded-full border border-purple-500/30">
                  <div class="text-white text-sm font-medium">Sakura Stream 18:30-18:45</div>
                </div>
                <div class="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                  胜利
                </div>
              </div>
              
              <div class="flex items-center space-x-4 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-xl p-4 border border-purple-500/30">
                <div class="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                  PK対戦
                </div>
                <div class="flex items-center space-x-3">
                  <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=40&q=60" 
                       alt="Neon Dance" class="w-8 h-8 rounded-full border border-purple-500/30">
                  <div class="text-white text-sm font-medium">Neon Dance 19:00-19:15</div>
                </div>
                <div class="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md animate-pulse">
                  进行中
                </div>
              </div>
            </div>

            <!-- 图表区域 -->
            <div class="h-80 bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl p-6 border border-gray-600" >
              <div v-if="chartLoading" class="h-full flex items-center justify-center">
                <div class="text-center">
                  <div class="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p class="text-gray-400 text-sm">加载图表数据中...</p>
                </div>
              </div>
              <div v-else class="h-full">
                <!-- ECharts 图表容器 -->
                <div id="engagement-chart" class="w-full h-full"></div>
              </div>
            </div>
          </div>

          <!-- AI高光聊天 -->
          <div v-if="false" class="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-700">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-white font-bold text-2xl">AI高光聊天</h3>
              <button 
                @click="refreshData"
                class="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-xl text-sm font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-purple-500/25"
              >
                实时更新
              </button>
            </div>
            
            <div class="space-y-4">
              <div 
                v-for="highlight in aiHighlights" 
                :key="highlight.id"
                class="highlight-card bg-gradient-to-r from-gray-700/50 to-gray-800/50 rounded-xl p-5 border border-gray-600/50 hover:border-purple-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10 cursor-pointer"
                @click="playHighlight(highlight)"
              >
                <div class="flex items-start space-x-4">
                  <!-- 视频缩略图 -->
                  <div class="relative flex-shrink-0">
                    <div class="w-32 h-20 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg overflow-hidden border border-purple-500/30">
                      <img 
                        :src="highlight.thumbnail || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=60'" 
                        :alt="highlight.message"
                        class="w-full h-full object-cover"
                      >
                      <!-- 播放按钮 -->
                      <div class="absolute inset-0 flex items-center justify-center">
                        <div class="play-button w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg opacity-80 hover:opacity-100 transition-opacity">
                          <i class="fas fa-play text-white text-sm ml-0.5"></i>
                        </div>
                      </div>
                      <!-- 时长 -->
                      <div class="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                        {{ formatDuration(highlight.time) }}
                      </div>
                    </div>
                  </div>
                  
                  <div class="flex-1">
                    <div class="flex items-center space-x-3 mb-3">
                      <img :src="highlight.avatar" :alt="highlight.name" class="w-7 h-7 rounded-full border border-purple-500/30">
                      <span class="text-white text-sm font-semibold">{{ highlight.name }}</span>
                      <span class="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">{{ highlight.type }}</span>
                    </div>
                    <p class="text-gray-200 text-sm mb-3 leading-relaxed">{{ highlight.message }}</p>
                    <div class="flex items-center space-x-6 text-xs">
                      <span class="flex items-center text-red-400"><i class="fas fa-heart mr-1"></i>{{ highlight.likes }}</span>
                      <span class="flex items-center text-blue-400"><i class="fas fa-comment mr-1"></i>{{ highlight.comments }}</span>
                      <span class="flex items-center text-green-400"><i class="fas fa-share mr-1"></i>{{ highlight.shares }}</span>
                    </div>
                  </div>
                </div>
              </div>



    <!-- 视频播放模态框 -->
    <div v-if="showVideoModal" class="video-modal fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" @click.self="closeVideoModal">
      <div class="relative max-w-5xl w-full mx-4">
        <div class="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl overflow-hidden shadow-2xl border border-gray-700">
          <div class="p-6 border-b border-gray-600 flex items-center justify-between">
            <div>
              <h3 class="text-xl font-bold text-white">{{ selectedHighlight?.summary || '高光时刻' }}</h3>
              <p class="text-gray-400 text-sm mt-1">{{ selectedHighlight?.name }} • {{ formatDuration(selectedHighlight?.time) }}</p>
            </div>
            <button @click="closeVideoModal" class="text-gray-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-700">
              <i class="fas fa-times text-xl"></i>
            </button>
          </div>
          <div class="p-6">
            <div class="video-container relative">
              <video 
                v-if="selectedHighlight && selectedHighlight.clipFilePath"
                :src="selectedHighlight.clipFilePath"
                controls
                class="w-full h-96 object-cover rounded-lg shadow-lg"
                @error="onVideoError"
                preload="metadata"
              >
                您的浏览器不支持视频播放
              </video>
              <div v-else class="w-full h-96 bg-gray-700 rounded-lg flex items-center justify-center">
                <div class="text-center">
                  <i class="fas fa-video text-gray-400 text-4xl mb-4"></i>
                  <p class="text-gray-400">暂无视频内容</p>
                </div>
              </div>
            </div>
            <div class="mt-6">
              <div class="flex items-center space-x-4 mb-4">
                <span class="flex items-center text-red-400">
                  <i class="fas fa-heart mr-2"></i>{{ selectedHighlight?.likes || 0 }}
                </span>
                <span class="flex items-center text-blue-400">
                  <i class="fas fa-comment mr-2"></i>{{ selectedHighlight?.comments || 0 }}
                </span>
                <span class="flex items-center text-green-400">
                  <i class="fas fa-share mr-2"></i>{{ selectedHighlight?.shares || 0 }}
                </span>
              </div>
              <p class="text-gray-300 leading-relaxed">{{ selectedHighlight?.message || selectedHighlight?.videoDescription }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
              
              <!-- 当没有高光数据时显示默认内容 -->
              <div v-if="aiHighlights.length === 0" class="text-center py-8">
                <div class="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i class="fas fa-comments text-gray-400 text-xl"></i>
                </div>
                <p class="text-gray-400 text-sm">暂无高光聊天数据</p>
              </div>
            </div>
            
            <div class="mt-6 p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl border border-yellow-500/30">
              <div class="flex items-center">
                <div class="w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center mr-3">
                  <i class="fas fa-lightbulb text-yellow-400"></i>
                </div>
                <span class="text-gray-200 text-sm font-medium">提示:这些高光对话平均获得198个互动,是普通消息的3.2倍</span>
              </div>
            </div>
          </div>

          <!-- 礼物贡献分布 -->
          <div class="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-700">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-white font-bold text-2xl">打赏贡献分布</h3>
              <div v-if="contributionStats.loading" class="flex items-center text-gray-400 text-sm">
                <div class="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                加载中...
              </div>
            </div>
            
            <div v-if="contributionStats.loading" class="text-center py-8">
              <div class="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p class="text-gray-400 text-sm">加载贡献分布数据中...</p>
            </div>
            
            <div v-else-if="contributionStats.data.length > 0" class="flex items-center space-x-6">
              <!-- 饼图区域 -->
              <div class="flex-1">
                <div class="relative w-32 h-32 mx-auto mb-4">
                  <svg class="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
                    <circle 
                      v-for="(item, index) in contributionStats.data" 
                      :key="item.name"
                      cx="50" 
                      cy="50" 
                      r="40" 
                      fill="none" 
                      :stroke="getContributionColor(index)"
                      stroke-width="8" 
                      :stroke-dasharray="`${item.percentage * 2.51} 251`" 
                      :stroke-dashoffset="getContributionOffset(index)"
                    />
                  </svg>
                  <div class="absolute inset-0 flex items-center justify-center">
                    <span class="text-white font-bold text-lg">{{ getTopContributionPercentage() }}%</span>
                  </div>
                </div>
                <!-- 图例 -->
                <div class="space-y-2 text-sm">
                  <div 
                    v-for="(item, index) in contributionStats.data" 
                    :key="item.name"
                    class="flex items-center justify-between"
                  >
                    <div class="flex items-center">
                      <div class="w-3 h-3 rounded-full mr-2" :style="{ backgroundColor: getContributionColor(index) }"></div>
                      <span class="text-gray-300">{{ item.name }}</span>
                    </div>
                    <span class="text-white font-semibold">{{ item.percentage }}%</span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 无数据状态 -->
            <div v-else class="text-center py-8">
              <div class="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <i class="fas fa-chart-pie text-gray-400 text-xl"></i>
              </div>
              <p class="text-gray-400 text-sm">暂无贡献分布数据</p>
            </div>
          </div>

          <!-- 顶级送礼者排行榜 -->
          <div class="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-700">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-white font-bold text-2xl">金主排行榜</h3>
              <div v-if="rewardListLoading" class="flex items-center text-gray-400 text-sm">
                <div class="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                加载中...
              </div>
            </div>
            
              <div id="reward-list-container" v-if="rewardList.length > 0" class="space-y-4 custom-scrollbar">
                <div v-for="(gifter, index) in rewardList" :key="gifter.userName + index"
                     class="reward-item bg-gradient-to-r from-gray-700/50 to-gray-800/50 rounded-xl p-4 border border-gray-600/50 hover:border-purple-500/50 transition-all duration-300">
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-4">
                    <!-- 排名图标 -->
                    <div class="flex items-center justify-center w-8 h-8 rounded-full" 
                         :class="index < 3 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' : 'bg-gray-600'">
                      <i v-if="index < 3" class="fas fa-crown text-white text-sm"></i>
                      <span v-else class="text-white text-sm font-bold">#{{ index + 1 }}</span>
                    </div>
                    <!-- 用户信息 -->
                    <div class="flex items-center space-x-3">
                      <img
                        :src="gifter.avatarUrl"
                        :alt="gifter.userName"
                        class="w-8 h-8 rounded-full flex-shrink-0 border border-purple-500/30 object-cover"
                        @error="handleImageError"
                      >
                      <div>
                        <div 
                          class="text-white font-semibold text-sm cursor-pointer hover:text-purple-300 transition-colors"
                          @click="copyToClipboard(gifter.userName)"
                        >
                          {{ gifter.userName }}
                        </div>
                        <div class="text-gray-400 text-xs">Session: {{ gifter.sessionId }}</div>
                      </div>
                    </div>
                  </div>
                  <!-- 金额 -->
                  <div class="text-right">
                    <div class="text-white font-bold text-lg">{{ gifter.coins }} coins</div>
                    <div class="text-gray-400 text-sm">排名 #{{ index + 1 }}</div>
                  </div>
                </div>
              </div>
              
              <!-- 加载更多提示 -->
              <div v-if="rewardListLoadingMore" class="text-center py-4">
                <div class="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p class="text-gray-400 text-xs">加载更多...</p>
              </div>
              <div v-else-if="hasMoreRewardData" class="text-center py-4">
                <p class="text-gray-400 text-xs">继续滚动加载更多</p>
              </div>
              <div v-else-if="rewardList.length >= 100" class="text-center py-4">
                <p class="text-gray-400 text-xs">已加载全部数据</p>
              </div>
            </div>
            
            <!-- 无数据状态 -->
            <div v-else-if="!rewardListLoading" class="text-center py-8">
              <div class="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <i class="fas fa-trophy text-gray-400 text-xl"></i>
              </div>
              <p class="text-gray-400 text-sm">暂无金主数据</p>
            </div>
          </div>



          <!-- AI高光评分 -->
          <div v-if="false" class="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-700">
            <h3 class="text-white font-bold text-2xl mb-6">AI高光评分</h3>
            <div class="text-center mb-8">
              <div class="text-5xl font-bold text-white mb-3">87/100</div>
              <div class="flex justify-center space-x-2">
                <i v-for="i in 5" :key="i" :class="i <= 4 ? 'fas fa-star text-yellow-400 text-xl' : 'far fa-star text-gray-600 text-xl'"></i>
              </div>
            </div>
            
            <div class="space-y-5">
              <div v-for="score in aiScores" :key="score.name">
                <div class="flex items-center justify-between mb-3">
                  <span class="text-gray-200 text-sm font-medium">{{ score.name }}</span>
                  <span class="text-white text-sm font-bold">{{ score.value }}%</span>
                </div>
                <div class="w-full bg-gray-700 rounded-full h-3 shadow-inner">
                  <div 
                    class="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all duration-500 shadow-lg"
                    :style="{ width: score.value + '%' }"
                  ></div>
                </div>
              </div>
            </div>
            
            <div class="mt-6 p-4 bg-gradient-to-r from-red-500/10 to-pink-500/10 rounded-xl border border-red-500/30">
              <div class="flex items-center">
                <div class="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center mr-3">
                  <i class="fas fa-exclamation-circle text-red-400"></i>
                </div>
                <span class="text-gray-200 text-sm font-medium">在19:12检测到峰值时刻 - 观众激增+156%</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧边栏 - 实时聊天 -->
        <div class="xl:col-span-3 space-y-4 md:space-y-6">
          <div class="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-700 h-fit">
            <!-- 直播状态指示器 -->
            <div v-if="isLive" class="mb-6">
              <div class="bg-gradient-to-r from-red-500/20 to-red-600/20 rounded-xl p-4 border border-red-500/30 flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <span class="text-white font-semibold text-sm">LIVE</span>
                </div>
                <div class="text-red-300 text-xs font-medium">直播中</div>
              </div>
            </div>
            
            <!-- Top Gifters -->
            <div class="mb-6">
              <h4 class="text-gray-200 text-base font-semibold mb-4">Top Gifters</h4>
              <div v-if="topGiftersLoading" class="text-center py-4">
                <div class="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p class="text-gray-400 text-xs">加载中...</p>
              </div>
              <div v-else-if="topGiftersList.length > 0" class="space-y-3">
                <div v-for="gifter in topGiftersList" :key="gifter.id" class="flex items-center justify-between bg-gray-700/50 rounded-lg p-3 hover:bg-gray-700/70 transition-colors">
                  <div class="flex items-center space-x-3">
                    <div class="w-6 h-6 rounded-full overflow-hidden border border-purple-500/30 flex-shrink-0">
                      <img
                        :src="gifter.avatarUrl || ''"
                        :alt="gifter.userName"
                        class="w-full h-full object-cover"
                        @error="handleImageError"
                      >
                    </div>
                    <span class="text-gray-300 text-sm font-medium">#{{ gifter.rank }} {{ gifter.userName }}</span>
                  </div>
                  <span class="text-white text-sm font-bold">{{ formatNumber(gifter.coins) }}</span>
                </div>
              </div>
              <div v-else class="text-center py-4">
                <p class="text-gray-400 text-xs">暂无数据</p>
              </div>
            </div>

            <!-- 情感反应 -->
            <div class="mb-6">
              <h4 class="text-gray-200 text-base font-semibold mb-4">Sentiment Reactions</h4>

              <div class="grid grid-cols-2 gap-3 mb-6">
                <div class="sentiment-card bg-gradient-to-r from-purple-600/20 to-purple-500/20 rounded-lg p-3 border border-purple-500/30">
                  <div class="flex items-center justify-between">
                    <span class="text-purple-300 text-sm font-medium">~ #かわいい</span>
                    <span class="text-white text-sm font-bold sentiment-badge px-2 py-1 rounded-full">38</span>
                  </div>
                </div>
                <div class="sentiment-card bg-gradient-to-r from-pink-600/20 to-pink-500/20 rounded-lg p-3 border border-pink-500/30">
                  <div class="flex items-center justify-between">
                    <span class="text-pink-300 text-sm font-medium">😍</span>
                    <span class="text-white text-sm font-bold sentiment-badge px-2 py-1 rounded-full">52</span>
                  </div>
                </div>
              </div>

              <!-- 聊天评论列表 -->
              <div v-if="commentsLoading" class="text-center py-4">
                <div class="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p class="text-gray-400 text-xs">Loading chat messages...</p>
              </div>
              <div v-else class="space-y-3 max-h-80 overflow-y-auto custom-scrollbar">
                <div
                  v-for="(comment, index) in commentsList"
                  :key="comment.id || index"
                  class="comment-item"
                >
                  <div class="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-700/30 transition-colors">
                    <img
                      :src="comment.avatar || 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'"
                      :alt="comment.userName"
                      class="w-8 h-8 rounded-full flex-shrink-0 border border-purple-500/30"
                      @error="handleImageError"
                    >
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center space-x-2 mb-1">
                        <span class="text-white text-sm font-semibold">{{ comment.userName }}</span>
                        <span class="text-gray-400 text-xs bg-gray-700 px-2 py-1 rounded">Lv.{{ comment.userLevel }}</span>
                        <span class="text-gray-500 text-xs">{{ formatTimestamp(comment.timestamp) }}</span>
                      </div>
                      <p class="text-gray-200 text-sm leading-relaxed break-words">{{ comment.content }}</p>
                    </div>
                  </div>
                </div>

              </div>
              <!-- 无数据状态 -->
              <div v-if="!commentsLoading && commentsList.length === 0" class="text-center py-4">
                <p class="text-gray-400 text-xs">暂无数据</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 复制提示 -->
    <div v-if="copyToast.show" class="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999]">
      <div class="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-lg shadow-2xl border border-white/20 flex items-center space-x-3 animate-pulse">
        <i class="fas fa-check-circle text-xl"></i>
        <span class="font-semibold">{{ copyToast.message }}</span>
      </div>
    </div>

  </div>
</template>

<script>
import creatorService from '@/services/creatorService'
import * as echarts from 'echarts'

export default {
  name: 'StreamerStats',
  data() {
    return {
      loading: true,
      error: null,
      selectedTimelineTab: 'this-session',
      selectedMetric: 'all',
      timelineTabs: [
        { label: '本次直播', value: 'this-session' },
        { label: '周对比', value: 'week-on-week' },
        { label: '月对比', value: 'month-on-month' }
      ],
      metrics: [
        { label: '全部指标', value: 'all' },
        { label: '在线用户', value: 'online-users' },
        { label: '点赞', value: 'likes' },
        { label: '互动', value: 'interactions' }
      ],
      // 复制提示相关
      copyToast: {
        show: false,
        message: '',
        type: 'success'
      },
      // ECharts 实例
      chartInstance: null,
      streamerInfo: {
        id: '',
        name: '',
        avatar: '',
        socialHandle: '',
        level: '',
        category: '',
        followers: 0,
        income: 0,
        liveHours: 0,
        avgViewers: 0,
        lastLive: '',
        totalSessions: 0,
        successRate: 0
      },
      liveSessionData: {
        schedule: '',
        sessions: []
      },
      // 视频播放相关
      showVideoModal: false,
      selectedHighlight: null,
      // 分页相关
      currentPage: 1,
      pageSize: 10,
      sortField: 'timestamp',
      sortOrder: 'desc',
      // 当前选中的场次
      selectedSessionId: null,
      // 金主排行榜数据
      rewardList: [],
      rewardListLoading: false,
      rewardListLoadingMore: false,
      rewardListPageIndex: 1,
      rewardListPageSize: 10,
      hasMoreRewardData: true,
      // Top Gifters数据
      topGiftersList: [],
      topGiftersLoading: false,
      // 图表数据
      chartData: {
        onlineUsers: [],
        likeCount: [],
        xAxis: [],
        interactions: []
      },
      chartLoading: false,
      // 评论列表数据
      commentsList: [],
      commentsLoading: false,
      selectedMessageType: 'chat',
      currentCommentPage: 1,
      commentPageSize: 10,
      // 打赏贡献分布数据
      contributionStats: {
        loading: false,
        data: [],
        totalAmount: 0
      },
      aiHighlights: [
        {
          id: 1,
          name: 'StarGifter',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          time: 38,
          type: 'Top gifter + high engagement',
          message: 'Your dance moves are absolutely incredible! This is the best stream I\'ve seen all week! ✨',
          clipFilePath: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
          thumbnail: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=60',
          likes: 234,
          comments: 28,
          shares: 15
        },
        {
          id: 2,
          name: 'MikaLive+',
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          time: 38,
          type: 'Trending question (x47 similar)',
          message: 'Can you teach us that choreography? Everyone wants to learn it! 💃',
          clipFilePath: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
          thumbnail: 'https://images.unsplash.com/photo-1518834107812-67b0b7c58434?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=60',
          likes: 189,
          comments: 23,
          shares: 0
        },
        {
          id: 3,
          name: 'TokyoDreamer',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          time: 38,
          type: 'Viral moment trigger',
          message: 'This is why you\'re my favorite creator. Pure talent and energy! ✨🔥',
          clipFilePath: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
          thumbnail: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=60',
          likes: 312,
          comments: 15,
          shares: 18
        },
        {
          id: 4,
          name: 'NeonQueen',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          time: 38,
          type: 'High sentiment score (98%)',
          message: 'How do you keep this energy level?? Teach me your secrets! 🔥',
          clipFilePath: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
          thumbnail: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=60',
          likes: 156,
          comments: 12,
          shares: 0
        }
      ],
      aiScores: [
        { name: '兴奋度', value: 92 },
        { name: '聊天活跃度', value: 85 },
        { name: '送礼', value: 78 },
        { name: '参与度', value: 94 }
      ],
      topGifters: [
        { rank: 1, name: 'StarGifter', amount: '2840' },
        { rank: 2, name: 'MikaLive+', amount: '1950' },
        { rank: 3, name: 'DiamondQueen', amount: '1620' }
      ],
      detailedTopGifters: [
        { 
          rank: 1, 
          name: 'DiamondQueen', 
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          amount: '2,840', 
          giftCount: 142,
          percentage: 35
        },
        { 
          rank: 2, 
          name: 'StarGifter', 
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          amount: '1,950', 
          giftCount: 98,
          percentage: 24
        },
        { 
          rank: 3, 
          name: 'MikaLive+', 
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          amount: '1,620', 
          giftCount: 81,
          percentage: 20
        },
        { 
          rank: 4, 
          name: 'GoldenFan', 
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          amount: '980', 
          giftCount: 49,
          percentage: 12
        }
      ],
      chatMessages: [
        {
          id: 1,
          name: 'MikaLive+',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          level: 42,
          time: '19:32',
          content: 'Can you do that dance again?',
          isFan: true,
          giftCount: 5
        },
        {
          id: 2,
          name: 'TokioChan',
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          level: 15,
          time: '19:31',
          content: 'Amazing performance!',
          isFan: false,
          giftCount: 0
        },
        {
          id: 3,
          name: 'StarGifter',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          level: 48,
          time: '19:30',
          content: 'Sending love! ✨',
          isFan: true,
          giftCount: 12
        },
        {
          id: 4,
          name: 'YukoFan',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          level: 8,
          time: '19:29',
          content: 'Best streamer ever!',
          isFan: true,
          giftCount: 0
        },
        {
          id: 5,
          name: 'NeonNinja',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          level: 3,
          time: '19:28',
          content: 'Just joined, what did I miss?',
          isFan: false,
          giftCount: 0
        }
      ],
      enhancedChatMessages: [
        {
          id: 1,
          name: 'MikaLive+',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          level: 42,
          time: '19:32',
          content: 'This stream is amazing! 💕',
          isFan: true,
          giftCount: 5,
          viewers: 2049
        },
        {
          id: 2,
          name: 'TokioChan',
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          level: 28,
          time: '19:31',
          content: 'Can you do that dance again?',
          isFan: false,
          giftCount: 0,
          viewers: 1950
        },
        {
          id: 3,
          name: 'StarGifter',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          level: 48,
          time: '19:30',
          content: 'Sending love! ✨',
          isFan: true,
          giftCount: 12,
          viewers: 1820
        },
        {
          id: 4,
          name: 'YukoFan',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          level: 12,
          time: '19:29',
          content: 'Best streamer ever!',
          isFan: true,
          giftCount: 0
        },
        {
          id: 5,
          name: 'NeonNinja',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
          level: 3,
          time: '19:28',
          content: 'Just joined, what did I miss?',
          isFan: false,
          giftCount: 0
        }
      ]
    }
  },
  computed: {
    // 判断是否正在直播
    isLive() {
      if (!this.liveSessionData.sessions || this.liveSessionData.sessions.length === 0) {
        return false;
      }
      
      // 获取最新的直播时段
      const latestSession = this.liveSessionData.sessions[0];
      if (!latestSession || !latestSession.sessionId) {
        return false;
      }
      
      // 解析sessionId格式：2025101715 -> 2025-10-17 15:00:00
      const sessionIdStr = String(latestSession.sessionId);
      if (sessionIdStr.length !== 10) {
        return false;
      }
      
      try {
        const year = parseInt(sessionIdStr.substring(0, 4));
        const month = parseInt(sessionIdStr.substring(4, 6)) - 1; // 月份从0开始
        const day = parseInt(sessionIdStr.substring(6, 8));
        const hour = parseInt(sessionIdStr.substring(8, 10));
        
        // 假设直播时长为2小时
        const startTime = new Date(year, month, day, hour, 0, 0);
        const endTime = new Date(startTime.getTime() + 2 * 60 * 60 * 1000); // 2小时后
        
        const now = new Date();
        
        // 如果当前时间在直播开始和结束时间之间，或者与结束时间相差1分钟内，则认为是直播中
        const timeDiff = Math.abs(now.getTime() - endTime.getTime());
        return (now >= startTime && now <= endTime) || timeDiff <= 60 * 1000; // 1分钟内
      } catch (error) {
        console.warn('解析直播时间失败:', error);
        return false;
      }
    },
    
    // 计算图表的最大值，用于Y轴刻度
    chartMaxValue() {
      const allValues = [...this.chartData.onlineUsers, ...this.chartData.likeCount];
      const max = Math.max(...allValues, 0);
      return max > 0 ? Math.ceil(max * 1.2) : 1000;
    },
    
    // 计算Y轴刻度标签
    chartYAxisLabels() {
      const max = this.chartMaxValue;
      return [
        Math.floor(max * 0.8),
        Math.floor(max * 0.6),
        Math.floor(max * 0.4),
        Math.floor(max * 0.2),
        0
      ];
    },
    
    // 生成图表路径
    chartPath() {
      if (this.chartData.onlineUsers.length === 0) {
        return "M 20 200 Q 60 180 100 160 T 180 140 T 260 120 T 340 100 T 380 80";
      }
      
      const data = this.selectedMetric === 'online-users' ? this.chartData.onlineUsers : this.chartData.likeCount;
      const max = this.chartMaxValue;
      const width = 360;
      const height = 220;
      const padding = 20;
      
      if (data.length === 0) return "";
      
      const stepX = width / Math.max(data.length - 1, 1);
      let path = "";
      
      data.forEach((value, index) => {
        const x = padding + index * stepX;
        const y = padding + height - (value / max) * height;
        
        if (index === 0) {
          path += `M ${x} ${y}`;
        } else {
          path += ` L ${x} ${y}`;
        }
      });
      
      return path;
    },
    
    // 生成填充区域路径
    chartFillPath() {
      const path = this.chartPath;
      if (!path) return "";
      
      const width = 360;
      const height = 220;
      const padding = 20;
      
      return `${path} L ${padding + width} ${padding + height} L ${padding} ${padding + height} Z`;
    },
    
    // 生成数据点
    chartPoints() {
      if (this.chartData.onlineUsers.length === 0) {
        return [
          { x: 20, y: 200 },
          { x: 100, y: 160 },
          { x: 180, y: 140 },
          { x: 260, y: 120 },
          { x: 340, y: 100 },
          { x: 380, y: 80 }
        ];
      }
      
      const data = this.selectedMetric === 'online-users' ? this.chartData.onlineUsers : this.chartData.likeCount;
      const max = this.chartMaxValue;
      const width = 360;
      const height = 220;
      const padding = 20;
      
      return data.map((value, index) => {
        const x = padding + index * (width / Math.max(data.length - 1, 1));
        const y = padding + height - (value / max) * height;
        return { x, y };
      });
    }
  },
  async created() {
    // 从路由参数获取主播ID，支持多种方式
    let streamerId = this.$route.params.id;
    
    // 如果没有路由参数，尝试从查询参数获取
    if (!streamerId) {
      streamerId = this.$route.query.id || this.$route.query.streamerId;
    }
    
    // 如果还是没有，使用默认值
    if (!streamerId) {
      streamerId = '1mb.girls'; // 默认主播ID
    }

    await this.loadStreamerData(streamerId);
  },
  mounted() {
    console.log('组件已挂载，开始初始化图表...');

    // 添加键盘事件监听
    document.addEventListener('keydown', this.handleKeydown);
    // 添加页面滚动监听
    window.addEventListener('scroll', this.handleWindowScroll);

    // 不在这里初始化图表，等待数据加载完成
  },
  beforeDestroy() {
    // 移除键盘事件监听
    document.removeEventListener('keydown', this.handleKeydown);
    // 移除页面滚动监听
    window.removeEventListener('scroll', this.handleWindowScroll);
    // 销毁 ECharts 实例
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  },
  watch: {
    selectedMetric: {
      handler() {
        this.$nextTick(() => {
          this.updateChart();
        });
      },
      immediate: true
    },
    chartData: {
      handler() {
        this.$nextTick(() => {
          this.updateChart();
        });
      },
      deep: true
    }
  },
  methods: {
    async loadStreamerData(streamerId) {
      this.loading = true;
      this.error = null;
      
      try {
        // 1. 获取创作者基本信息（头像昵称信息）
        const detailsData = await creatorService.getCreatorDetails(streamerId)
        const creatorDetail = detailsData.data?.find(item => item.username === streamerId)

        // 2. 获取创作者真实ID（通过社交账号）
        let realCreatorId = streamerId; // 默认值
        try {
          const creatorResponse = await creatorService.getCreatorBySocialName(streamerId, 'tiktok');
          if (creatorResponse && creatorResponse.success && creatorResponse.data) {
            realCreatorId = creatorResponse.data.id;
          }
        } catch (error) {
          console.warn('获取创作者真实ID失败，使用默认值:', error);
        }

        if (creatorDetail) {
          const creator = creatorDetail;
          this.streamerInfo = {
            id: realCreatorId, // 使用新接口获取的真实ID
            name: creator.nickname || creator.preferredName || 'Unknown',
            avatar: creator.avatarUrl || '',
            socialHandle: creator.username || '',
            level: creator.level || '',
            category: creator.category || '',
            followers: creator.followerCount || 0,
            income: creator.income || 0,
            liveHours: creator.liveHours || 0,
            avgViewers: creator.avgViewers || 0,
            lastLive: creator.lastLive || '',
            totalSessions: creator.totalSessions || 0,
            successRate: creator.successRate || 0
          };
        }

        // 3. 获取直播会话摘要数据（Schedule区域）
        const sessionSummary = await creatorService.getLiveSessionSummary(streamerId);
        if (sessionSummary && sessionSummary.data) {
          console.log('原始会话数据:', sessionSummary.data);
          
          // 处理重复的 sessionId，确保每个 session 都有唯一的 key
          const uniqueSessions = this.processSessions(sessionSummary.data);
          console.log('处理后的会话数据:', uniqueSessions);
          
          this.liveSessionData = {
            schedule: sessionSummary.data.schedule || 'Mon-Fri, 19:00-21:00 JST',
            sessions: uniqueSessions
          };
          
          // 自动选择第一个场次
          if (this.liveSessionData.sessions.length > 0) {
            await this.selectSession(this.liveSessionData.sessions[0]);
          }
        }

        // 3. AI高光聊天数据将在selectSession中加载
        
      } catch (error) {
        console.error('加载主播数据失败:', error);
        this.error = '加载主播数据失败，请重试';
      } finally {
        this.loading = false;
      }
    },
    
     /**
      * 刷新数据
      */
     async refreshData() {
       const streamerId = this.$route.params.id;
       await this.loadStreamerData(streamerId);
     },

     /**
      * 选择场次
      */
     async selectSession(session) {
       this.selectedSessionId = session.sessionId;
       // 根据选中的场次更新相关数据
       await this.loadSessionData(session.sessionId);
     },

     /**
      * 加载场次相关数据
      */
    async loadSessionData(sessionId) {
      if (!sessionId) return;

      try {
        console.log('开始加载场次数据，sessionId:', sessionId);
        // 加载金主排行榜数据
        await this.loadRewardList(sessionId);
        // 加载Top Gifters数据
        await this.loadTopGifters(sessionId);
        // 加载高光时刻数据
        await this.loadHighlightMoments(sessionId);
        // 加载评论列表数据
        await this.loadComments();
        // 加载图表数据
        await this.loadChartData();
        // 加载打赏贡献分布数据
        await this.loadContributionStats();
        console.log('场次数据加载完成');

        // 数据加载完成后初始化图表
        this.$nextTick(() => {
          setTimeout(() => {
            console.log('数据加载完成，开始初始化图表...');
            this.initChart();
          }, 500);
        });
      } catch (error) {
        console.error('加载场次数据失败:', error);
      }
    },

     /**
      * 加载金主排行榜数据
      */
     async loadRewardList(sessionId) {
       this.rewardListLoading = true;
       // 重置分页状态
       this.rewardListPageIndex = 1;
       // 强制设置为true以便测试无限加载功能
       this.hasMoreRewardData = true;

       try {
         const params = {
           liveRoomId: this.streamerInfo.socialHandle || this.$route.params.id,
           sessionId: sessionId,
           pageSize: 10,
           pageIndex: 1
         };

         // 调用真实的API接口
         const response = await creatorService.getRewardList(params.liveRoomId, sessionId, params.pageSize, params.pageIndex);

         if (response && response.success && response.data) {
           // 处理API返回的数据，添加排名信息和头像
           this.rewardList = response.data.map((item, index) => ({
             merchantSn: item.merchantSn,
             liveRoomId: item.liveRoomId,
             sessionId: item.sessionId,
             userName: item.userName,
             coins: item.coins,
             rowNum: item.rowNum || (index + 1),
             avatarUrl: item.avatarUrl || 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
           }));
         } else {
           // 如果API调用失败，使用默认数据
           this.rewardList = [
             {
               merchantSn: "243636276",
               liveRoomId: params.liveRoomId,
               sessionId: sessionId,
               userName: "🐰nao1129🐱☪️",
               coins: 945,
               rowNum: 1,
               avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
             },
             {
               merchantSn: "243636276",
               liveRoomId: params.liveRoomId,
               sessionId: sessionId,
               userName: "ふる",
               coins: 813,
               rowNum: 2,
               avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
             },
             {
               merchantSn: "243636276",
               liveRoomId: params.liveRoomId,
               sessionId: sessionId,
               userName: "サイエン",
               coins: 279,
               rowNum: 3,
               avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
             },
             {
               merchantSn: "243636276",
               liveRoomId: params.liveRoomId,
               sessionId: sessionId,
               userName: "ひで",
               coins: 113,
               rowNum: 4,
               avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
             },
             {
               merchantSn: "243636276",
               liveRoomId: params.liveRoomId,
               sessionId: sessionId,
               userName: "黒👿",
               coins: 85,
               rowNum: 5,
               avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
             }
           ];
         }
       } catch (error) {
         console.error('加载金主排行榜失败:', error);
         this.rewardList = [];
       } finally {
         this.rewardListLoading = false;
       }
     },

    /**
     * 加载图表数据
     */
    async loadChartData() {
      this.chartLoading = true;
      try {
        const roomId = this.streamerInfo.socialHandle || this.$route.params.id;
        
        // 基于sessionId生成时间范围
        let startTimeStr, endTimeStr;
        
        if (this.selectedSessionId) {
          // 解析sessionId格式：2025101715 -> 2025-10-17 15:00:00
          const sessionIdStr = String(this.selectedSessionId);
          if (sessionIdStr.length === 10) {
            const year = sessionIdStr.substring(0, 4)
            const month = sessionIdStr.substring(4, 6)
            const day = sessionIdStr.substring(6, 8)
            const hour = sessionIdStr.substring(8, 10)
            
            startTimeStr = `${year}-${month}-${day} ${hour}:00:00`;
            endTimeStr = `${year}-${month}-${day} 23:59:59`;
          } else {
            // 如果sessionId格式不正确，使用默认时间范围
            const endTime = new Date();
            const startTime = new Date(endTime.getTime() - 2 * 60 * 60 * 1000);
            startTimeStr = startTime.toISOString().replace('T', ' ').substring(0, 19);
            endTimeStr = endTime.toISOString().replace('T', ' ').substring(0, 19);
          }
        } else {
          // 如果没有选中场次，使用默认时间范围
          const endTime = new Date();
          const startTime = new Date(endTime.getTime() - 2 * 60 * 60 * 1000);
          startTimeStr = startTime.toISOString().replace('T', ' ').substring(0, 19);
          endTimeStr = endTime.toISOString().replace('T', ' ').substring(0, 19);
        }
        
        // 并行获取两个接口的数据
        const [roomStatisticsData, commentStatisticsData] = await Promise.all([
          creatorService.getLiveRoomStatistics(roomId, startTimeStr, endTimeStr),
          creatorService.getLiveCommentMinuteStatistics(roomId, startTimeStr, endTimeStr)
        ]);
        
        // 处理点赞和在线用户数据（与StreamerDetail.vue保持一致）
        let onlineUsers = [];
        let likeCount = [];
        let interactions = [];
        let xAxis = [];
        
        if (roomStatisticsData && roomStatisticsData.success && roomStatisticsData.data) {
          const series = roomStatisticsData.data.series || [];
          const onlineUsersSeries = series.find(s => s.name === 'onlineUsers');
          const likeCountSeries = series.find(s => s.name === 'likeCount');
          
          onlineUsers = onlineUsersSeries ? onlineUsersSeries.data : [];
          likeCount = likeCountSeries ? likeCountSeries.data : [];
          xAxis = roomStatisticsData.data.xaxis || [];
        }
        
        // 专门处理互动数据（从评论分钟统计接口获取）
        if (commentStatisticsData && commentStatisticsData.success && commentStatisticsData.data) {
          const series = commentStatisticsData.data.series || [];
          
          // 简单粗暴地累加四个维度的数值：gift、social、chat、enter
          if (series.length > 0) {
            // 使用commentStatisticsData的时间轴
            xAxis = commentStatisticsData.data.xaxis || [];
            
            // 初始化互动数组，与时间轴长度一致
            interactions = new Array(xAxis.length).fill(0);
            
            // 累加所有类型的评论数
            series.forEach(item => {
              if (item.commentNum && Array.isArray(item.commentNum)) {
                // 直接累加每个时间点的数值
                item.commentNum.forEach((count, index) => {
                  if (interactions[index] !== undefined) {
                    interactions[index] += count;
                  }
                });
              }
            });
          }
        }
        
        this.chartData = {
          onlineUsers,
          likeCount,
          interactions,
          xAxis
        };
        
      } catch (error) {
        console.error('加载图表数据失败:', error);
        // 设置默认数据
        this.chartData = {
          onlineUsers: [],
          likeCount: [],
          interactions: [],
          xAxis: []
        };
      } finally {
        this.chartLoading = false;
      }
    },

     /**
      * 加载上一页评论
      */
     async loadPreviousComments() {
       if (this.currentCommentPage > 1) {
         this.currentCommentPage--;
         await this.loadComments();
       }
     },

     /**
      * 加载下一页评论
      */
     async loadNextComments() {
       if (this.commentsList.length >= this.commentPageSize) {
         this.currentCommentPage++;
         await this.loadComments();
       }
     },

    
    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      return num.toLocaleString('zh-CN');
    },
    
    /**
     * 格式化时长
     */
     formatDuration(seconds) {
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },

     /**
      * 格式化时间戳
      */
     formatTimestamp(timestamp) {
       if (!timestamp) return '';

       try {
         // 如果是ISO字符串格式
         if (timestamp.includes('T')) {
           const date = new Date(timestamp);
           return date.toLocaleString('zh-CN', {
             month: '2-digit',
             day: '2-digit',
             hour: '2-digit',
             minute: '2-digit',
             hour12: false
           });
         }

         // 如果是其他格式的字符串
         const date = new Date(timestamp);
         if (!isNaN(date.getTime())) {
           return date.toLocaleString('zh-CN', {
             month: '2-digit',
             day: '2-digit',
             hour: '2-digit',
             minute: '2-digit',
             hour12: false
           });
         }

         return timestamp;
       } catch (error) {
         console.warn('时间戳格式化失败:', error);
         return timestamp || '';
       }
     },

    formatGiftValue(value) {
      if (!value) return '0'
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M+'
      } else if (value >= 10000) {
        return (value / 1000).toFixed(0) + 'K+'
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'K+'
      }
      return value.toLocaleString('zh-CN') + '+'
    },

    formatSessionTime(sessionId) {
      if (!sessionId) return '未知时间'
      
      try {
        // 确保sessionId是字符串类型
        const sessionIdStr = String(sessionId)
        
        // 处理长度为10的sessionId格式：2025083021 (年月日时)
        if (sessionIdStr.length === 10) {
          const year = sessionIdStr.substring(0, 4)
          const month = sessionIdStr.substring(4, 6)
          const day = sessionIdStr.substring(6, 8)
          const hour = sessionIdStr.substring(8, 10)
          
          return `${year}.${month}.${day} ${hour}:00`
        } else if (sessionIdStr.length >= 13) {
          // 如果是时间戳格式
          const timestamp = parseInt(sessionIdStr.substring(0, 13))
          const date = new Date(timestamp)
          return this.formatShortDate(date)
        } else {
          // 如果是其他格式，尝试解析
          return sessionIdStr.substring(0, 10) // 取前10位作为标识
        }
      } catch (error) {
        console.warn('解析sessionId时间失败:', error)
        return String(sessionId).substring(0, 10) || '未知'
      }
    },

    /**
     * 播放高光视频
     */
     playHighlight(highlight) {
      this.selectedHighlight = highlight
      this.showVideoModal = true
    },

     /**
      * 关闭视频模态框
      */
     closeVideoModal() {
       this.showVideoModal = false
       this.selectedHighlight = null
     },

     /**
      * 视频播放错误处理
      */
     onVideoError(event) {
       console.error('视频播放失败:', event)
       // 可以显示错误提示
       this.$message?.error('视频加载失败，请稍后重试')
     },

     /**
      * 键盘事件处理
      */
     handleKeydown(event) {
       if (event.key === 'Escape' && this.showVideoModal) {
         this.closeVideoModal()
       }
     },

     /**
      * 图片加载错误处理
      */
     handleImageError(event) {
       event.target.src = ''
     },


    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN');
    },
    
    formatTime(seconds) {
      if (!seconds) return '0:00';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = seconds % 60;
      
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
      } else {
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
      }
    },

    /**
     * 处理重复的 sessionId，合并互补的数据
     */
    processSessions(sessions) {
      if (!sessions || !Array.isArray(sessions)) {
        return [];
      }
      
      const sessionMap = new Map();
      
      sessions.forEach((session) => {
        const sessionId = session.sessionId || session.id;
        if (!sessionId) return;
        
        if (sessionMap.has(sessionId)) {
          // 合并重复的 sessionId 数据
          const existingSession = sessionMap.get(sessionId);
          const mergedSession = this.mergeSessionData(existingSession, session);
          sessionMap.set(sessionId, mergedSession);
        } else {
          // 第一次遇到这个 sessionId
          sessionMap.set(sessionId, { ...session });
        }
      });
      
      // 转换为数组并返回
      const uniqueSessions = Array.from(sessionMap.values());
      console.log('合并后的会话数据:', uniqueSessions);
      return uniqueSessions;
    },

    /**
     * 合并两个 session 数据，优先使用非零值
     */
    mergeSessionData(session1, session2) {
      const merged = { ...session1 };
      
      // 合并数值字段，优先使用非零值
      const numericFields = [
        'liveDuration', 'totalComments', 'uniqueCommenters', 
        'onlineUsers', 'likeCount', 'totalGiftQuantity', 'totalGiftValue'
      ];
      
      numericFields.forEach(field => {
        const value1 = session1[field] || 0;
        const value2 = session2[field] || 0;
        
        // 优先使用非零值，如果两个都有值则取最大值
        if (value1 === 0 && value2 !== 0) {
          merged[field] = value2;
        } else if (value2 === 0 && value1 !== 0) {
          merged[field] = value1;
        } else if (value1 !== 0 && value2 !== 0) {
          // 两个都有值，取最大值
          merged[field] = Math.max(value1, value2);
        } else {
          merged[field] = 0;
        }
      });
      
      // 合并其他字段，优先使用非空值
      const otherFields = ['merchantSn', 'roomId', 'title'];
      otherFields.forEach(field => {
        if (!merged[field] && session2[field]) {
          merged[field] = session2[field];
        }
      });
      
      return merged;
    },

    /**
     * 初始化 ECharts 图表
     */
    initChart() {
      const chartDom = document.getElementById('engagement-chart');
      if (!chartDom) {
        console.warn('图表容器未找到');
        return;
      }
      
      console.log('初始化ECharts图表...');
      this.chartInstance = echarts.init(chartDom);
      console.log('ECharts实例创建成功:', this.chartInstance);
      
      this.updateChart();
      
      // 监听窗口大小变化，自动调整图表大小
      window.addEventListener('resize', () => {
        if (this.chartInstance) {
          this.chartInstance.resize();
        }
      });
    },

    /**
     * 更新图表数据
     */
    updateChart() {
      if (!this.chartInstance) {
        console.warn('ECharts实例未初始化，无法更新图表');
        return;
      }

      console.log('更新图表数据...');
      console.log('当前图表数据:', this.chartData);
      console.log('选中的指标:', this.selectedMetric);
      
      const option = this.getChartOption();
      console.log('ECharts配置选项:', option);
      
      try {
        this.chartInstance.setOption(option, true);
        console.log('图表更新成功');
      } catch (error) {
        console.error('图表更新失败:', error);
      }
    },

    /**
     * 获取图表配置
     */
    getChartOption() {
      const isAllMetrics = this.selectedMetric === 'all';
      const hasData = this.chartData.onlineUsers.length > 0;
      
      // 生成时间轴数据
      const xAxisData = hasData 
        ? this.chartData.xAxis.map(time => {
            // 格式化时间显示
            const date = new Date(time);
            return `${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
          })
        : Array.from({ length: 12 }, (_, i) => `${18 + Math.floor(i/2)}:${i % 2 === 0 ? '00' : '30'}`);

      // 生成示例数据（当没有真实数据时）
      const sampleOnlineUsers = Array.from({ length: 12 }, (_, i) => 
        Math.floor(8000 + Math.sin(i * 0.5) * 2000 + Math.random() * 1000)
      );
      const sampleLikeCount = Array.from({ length: 12 }, (_, i) => 
        Math.floor(500 + Math.sin(i * 0.8) * 200 + Math.random() * 100)
      );

      const onlineUsersData = hasData ? this.chartData.onlineUsers : sampleOnlineUsers;
      const likeCountData = hasData ? this.chartData.likeCount : sampleLikeCount;
      const interactionsData = hasData ? this.chartData.interactions : Array.from({ length: 12 }, (_, i) => 
        Math.floor(200 + Math.sin(i * 0.6) * 100 + Math.random() * 50)
      );

      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(42, 34, 57, 0.9)',
          borderColor: 'rgba(129, 140, 248, 0.3)',
          textStyle: {
            color: '#ffffff'
          },
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: 'rgba(139, 92, 246, 0.5)'
            }
          }
        },
        legend: {
          data: isAllMetrics ? ['在线用户', '点赞数', '互动数'] : [this.selectedMetric === 'online-users' ? '在线用户' : this.selectedMetric === 'likes' ? '点赞数' : '互动数'],
          textStyle: {
            color: '#9ca3af'
          },
          right: 10,
          top: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData,
          axisLine: {
            lineStyle: {
              color: 'rgba(156, 163, 175, 0.3)'
            }
          },
          axisLabel: {
            color: '#9ca3af',
            fontSize: 11
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#9ca3af',
            fontSize: 11,
            formatter: (value) => {
              if (value >= 1000) {
                return (value / 1000).toFixed(0) + 'K';
              }
              return value;
            }
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(156, 163, 175, 0.1)',
              type: 'dashed'
            }
          }
        },
        series: [
          ...(isAllMetrics || this.selectedMetric === 'online-users' ? [{
            name: '在线用户',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            lineStyle: {
              width: 4,
              color: '#8b5cf6'
            },
            itemStyle: {
              color: '#8b5cf6',
              borderColor: '#ffffff',
              borderWidth: 3
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(139, 92, 246, 0.4)' },
                { offset: 1, color: 'rgba(139, 92, 246, 0.1)' }
              ])
            },
            data: onlineUsersData
          }] : []),
          ...(isAllMetrics || this.selectedMetric === 'likes' ? [{
            name: '点赞数',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            lineStyle: {
              width: 4,
              color: '#ec4899'
            },
            itemStyle: {
              color: '#ec4899',
              borderColor: '#ffffff',
              borderWidth: 3
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(236, 72, 153, 0.4)' },
                { offset: 1, color: 'rgba(236, 72, 153, 0.1)' }
              ])
            },
            data: likeCountData
          }] : []),
          ...(isAllMetrics || this.selectedMetric === 'interactions' ? [{
            name: '互动数',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            lineStyle: {
              width: 4,
              color: '#06b6d4'
            },
            itemStyle: {
              color: '#06b6d4',
              borderColor: '#ffffff',
              borderWidth: 3
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(6, 182, 212, 0.4)' },
                { offset: 1, color: 'rgba(6, 182, 212, 0.1)' }
              ])
            },
            data: interactionsData
          }] : [])
        ]
      };
    },

    /**
     * 处理页面滚动事件（金主列表无限加载）
     */
    async handleWindowScroll() {
      // 获取金主列表容器的位置
      const rewardContainer = document.getElementById('reward-list-container');
      if (!rewardContainer) return;

      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const containerBottom = rewardContainer.offsetTop + rewardContainer.offsetHeight;
      const scrollTrigger = containerBottom - scrollTop - windowHeight;

      console.log('滚动事件触发 - 金主列表容器检测:', {
        scrollTop,
        windowHeight,
        containerBottom,
        scrollTrigger,
        rewardListLoadingMore: this.rewardListLoadingMore,
        hasMoreRewardData: this.hasMoreRewardData,
        rewardListLength: this.rewardList.length
      });

      // 当金主列表容器距离页面底部还有200px时触发加载
      if (scrollTrigger <= 200 && !this.rewardListLoadingMore && this.hasMoreRewardData) {
        console.log('触发金主列表数据加载，容器底部距离页面底部:', scrollTrigger);
        await this.loadMoreRewardData();
      }
    },

    /**
     * 加载更多金主数据
     */
    async loadMoreRewardData() {
      // 检查是否正在加载或已达到上限
      if (this.rewardListLoadingMore || !this.hasMoreRewardData || this.rewardList.length >= 100) {
        return;
      }

      this.rewardListLoadingMore = true;
      try {
        this.rewardListPageIndex++;
        const roomId = this.streamerInfo.socialHandle || this.$route.params.id;
        
        const response = await creatorService.getRewardList(
          roomId, 
          this.selectedSessionId, 
          this.rewardListPageSize, 
          this.rewardListPageIndex
        );
        
        if (response && response.success && response.data && response.data.length > 0) {
          // 合并新数据
          const newData = response.data.map((item, index) => ({
            merchantSn: item.merchantSn,
            liveRoomId: item.liveRoomId,
            sessionId: item.sessionId,
            userName: item.userName,
            coins: item.coins,
            rowNum: item.rowNum || (this.rewardList.length + index + 1),
            avatarUrl: item.avatarUrl || 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
          }));
          
          this.rewardList = [...this.rewardList, ...newData];
          
          // 检查是否还有更多数据
          this.hasMoreRewardData = response.data.length >= this.rewardListPageSize && this.rewardList.length < 100;
        } else {
          this.hasMoreRewardData = false;
        }
      } catch (error) {
        console.error('加载更多金主数据失败:', error);
        this.hasMoreRewardData = false;
      } finally {
        this.rewardListLoadingMore = false;
      }
    },

    /**
     * 复制文本到剪贴板
     */
    async copyToClipboard(text) {
      console.log('复制方法被调用，原始文本:', text);

      try {
        await navigator.clipboard.writeText(text);
        console.log('剪贴板成功，显示提示');
        // 显示复制成功提示
        this.showCopyToast('已复制到剪贴板');
      } catch (error) {
        console.error('剪贴板API失败，回退方案:', error);
        // 备用方案：使用 document.execCommand
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        console.log('回退方案成功，显示提示');
        this.showCopyToast('已复制到剪贴板');
      }
    },

    /**
     * 加载Top Gifters数据
     */
    async loadTopGifters(sessionId) {
      this.topGiftersLoading = true;
      try {
        const roomId = this.streamerInfo.socialHandle || this.$route.params.id;
        
        // 调用API获取Top Gifters数据
        const response = await creatorService.getTopGifters(roomId, sessionId);
        
        if (response && response.success && response.data) {
          this.topGiftersList = response.data.map((item, index) => ({
            id: item.id || index,
            userName: item.userName,
            coins: item.coins,
            rank: index + 1,
            avatarUrl: item.avatarUrl || 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
          }));
        } else {
          // 如果API调用失败，使用默认数据（10条）
          this.topGiftersList = [
            {
              id: 1,
              userName: '🐰nao1129🐱☪️',
              coins: 945,
              rank: 1,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              id: 2,
              userName: 'ふる',
              coins: 813,
              rank: 2,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              id: 3,
              userName: 'サイエン',
              coins: 279,
              rank: 3,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              id: 4,
              userName: 'ひで',
              coins: 113,
              rank: 4,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              id: 5,
              userName: '黒👿',
              coins: 85,
              rank: 5,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              id: 6,
              userName: 'MikaLive+',
              coins: 75,
              rank: 6,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              id: 7,
              userName: 'StarGifter',
              coins: 68,
              rank: 7,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              id: 8,
              userName: 'TokyoDreamer',
              coins: 52,
              rank: 8,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              id: 9,
              userName: 'NeonQueen',
              coins: 45,
              rank: 9,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              id: 10,
              userName: 'GoldenFan',
              coins: 38,
              rank: 10,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            }
          ];
        }
      } catch (error) {
        console.error('加载Top Gifters数据失败:', error);
        this.topGiftersList = [];
      } finally {
        this.topGiftersLoading = false;
      }
    },

    /**
     * 加载高光时刻数据
     */
    async loadHighlightMoments(sessionId) {
      // 这里可以调用高光时刻的API
      // 暂时使用默认数据
      console.log('加载高光时刻数据，sessionId:', sessionId);
    },

    /**
     * 加载评论列表数据
     */
    async loadComments() {
      if (!this.selectedSessionId) return;

      this.commentsLoading = true;
      try {
        const roomId = this.streamerInfo.socialHandle || this.$route.params.id;

        const response = await creatorService.getLiveComments(
          this.selectedSessionId,
          roomId,
          this.currentCommentPage,
          this.commentPageSize,
          this.selectedMessageType
        );

        if (response && response.success && response.data) {
          // 处理API返回的数据
          this.commentsList = response.data.map((comment, index) => ({
            id: comment.id || `${this.selectedSessionId}-${this.currentCommentPage}-${index}`,
            userName: comment.nickname || comment.userId || 'Unknown User',
            content: comment.content || 'No content',
            messageType: comment.messageType || this.selectedMessageType,
            timestamp: comment.commentTime || comment.createdAt || 'Unknown time',
            avatar: comment.avatarUrl || 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
            userLevel: comment.userLevel || 1
          }));
        } else {
          // 如果API调用失败，使用默认数据
          this.commentsList = [
            {
              id: 1,
              userName: 'MikaLive+',
              content: 'This stream is amazing! 💕',
              timestamp: new Date().toISOString(),
              userLevel: 42,
              avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              id: 2,
              userName: 'TokioChan',
              content: 'Can you do that dance again?',
              timestamp: new Date().toISOString(),
              userLevel: 28,
              avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              id: 3,
              userName: 'StarGifter',
              content: 'Sending love! ✨',
              timestamp: new Date().toISOString(),
              userLevel: 48,
              avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            }
          ];
        }
      } catch (error) {
        console.error('加载评论数据失败:', error);
        this.commentsList = [];
      } finally {
        this.commentsLoading = false;
      }
    },

    /**
     * 加载打赏贡献分布数据
     */
    async loadContributionStats() {
      if (!this.selectedSessionId) return;

      this.contributionStats.loading = true;
      try {
        const roomId = this.streamerInfo.socialHandle || this.$route.params.id;
        
        const response = await creatorService.getContributionStats(roomId, this.selectedSessionId);
        
        if (response && response.success && response.data) {
          // 处理API返回的数据，适配新的数据结构
          this.contributionStats.data = response.data.map(item => ({
            name: item.contributionSegment,
            userNum: item.userNum,
            percentage: parseFloat(item.userPercent) || 0
          }));
          
          // 计算总用户数
          this.contributionStats.totalUsers = this.contributionStats.data.reduce((total, item) => {
            return total + (item.userNum || 0);
          }, 0);
        } else {
          // 如果API调用失败，使用默认数据
          this.contributionStats.data = [
            { name: '20%~40%', userNum: 1, percentage: 0.78 },
            { name: '40%~60%', userNum: 1, percentage: 0.78 },
            { name: '60%~80%', userNum: 3, percentage: 2.34 },
            { name: '后20%', userNum: 123, percentage: 96.09 }
          ];
          this.contributionStats.totalUsers = 128;
        }
      } catch (error) {
        console.error('加载打赏贡献分布数据失败:', error);
        // 使用默认数据
        this.contributionStats.data = [
          { name: '20%~40%', userNum: 1, percentage: 0.78 },
          { name: '40%~60%', userNum: 1, percentage: 0.78 },
          { name: '60%~80%', userNum: 3, percentage: 2.34 },
          { name: '后20%', userNum: 123, percentage: 96.09 }
        ];
        this.contributionStats.totalUsers = 128;
      } finally {
        this.contributionStats.loading = false;
      }
    },

    /**
     * 获取贡献分布颜色
     */
    getContributionColor(index) {
      const colors = ['#ec4899', '#8b5cf6', '#06b6d4', '#3b82f6', '#6b7280'];
      return colors[index] || colors[colors.length - 1];
    },

    /**
     * 获取贡献分布偏移量
     */
    getContributionOffset(index) {
      if (index === 0) return 0;
      
      let offset = 0;
      for (let i = 0; i < index; i++) {
        offset -= (this.contributionStats.data[i]?.percentage || 0) * 2.51;
      }
      return offset;
    },

    /**
     * 获取最高贡献百分比
     */
    getTopContributionPercentage() {
      if (this.contributionStats.data.length === 0) return 0;
      return Math.max(...this.contributionStats.data.map(item => item.percentage));
    },

    /**
     * 显示复制提示
     */
    showCopyToast(message) {
      this.copyToast = {
        show: true,
        message: message,
        type: 'success'
      };
      
      // 3秒后自动隐藏
      setTimeout(() => {
        this.copyToast.show = false;
      }, 3000);
    }
  }
}
</script>

<style scoped>

.glass-panel{
  background: rgba(42, 34, 57, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(129, 140, 248, 0.2);
  border-radius: 1rem;
  padding: 1rem;

  border-radius: 1rem;
    border-width: 1px;
    border-color: hsl(var(--glass-border) / .5);
    background-color: hsl(var(--glass-bg) / .4);
    --tw-backdrop-blur: blur(24px);
    -webkit-backdrop-filter: var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia);
    backdrop-filter: var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia);
}

.streamer-detail {
  background: #2a2239;
  min-height: calc(100vh - 64px);
  padding-bottom:30px
}

/* 评论列表自定义滚动条样式 */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.3);
  border-radius: 4px;
  border: 2px solid transparent;
  background-clip: content-box;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.5);
  background-clip: content-box;
}

/* 动画效果 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s ease-out;
}

/* 渐变文字效果 */
.gradient-text {
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* 卡片悬停效果 */
.card-hover {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-hover:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* 发光效果 */
.glow-purple {
  box-shadow: 0 0 20px rgba(139, 92, 246, 0.3);
}

.glow-blue {
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
}

.grid {
  display: grid;
}

.grid-cols-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}

.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.grid-cols-12 {
    grid-template-columns: repeat(12, minmax(0px, 1fr));
}

@media (min-width: 768px) {
  .md\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .md\:grid-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .md\:col-span-2 {
    grid-column: span 2 / span 2;
  }

  .md\:space-y-6 {
    margin-top: 1.5rem;
  }
}

@media (min-width: 1024px) {
  .lg\:grid-cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .lg\:col-span-2 {
    grid-column: span 2 / span 2;
  }
}

@media (min-width: 1280px) {
  .xl\:grid-cols-12 {
    grid-template-columns: repeat(12, minmax(0, 1fr));
  }

  .xl\:col-span-3 {
    grid-column: span 3 / span 3;
  }

  .xl\:col-span-6 {
    grid-column: span 6 / span 6;
  }

  .xl\:space-y-6 {
    margin-top: 1.5rem;
  }
}

.gap-6 {
  gap: 1.5rem;
}

.gap-8 {
  gap: 2rem;
}

.space-y-3 > * + * {
  margin-top: 0.75rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.space-y-6 > * + * {
  margin-top: 1.5rem;
}

.space-y-8 > * + * {
  margin-top: 2rem;
}

.space-x-2 > * + * {
  margin-left: 0.5rem;
}

.space-x-3 > * + * {
  margin-left: 0.75rem;
}

.space-x-4 > * + * {
  margin-left: 1rem;
}

.space-x-6 > * + * {
  margin-left: 1.5rem;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-3 {
  margin-bottom: 0.75rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.mb-8 {
  margin-bottom: 2rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.p-4 {
  padding: 1rem;
}

.p-6 {
  padding: 1.5rem;
}

.p-8 {
  padding: 2rem;
}

.px-3 {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.px-4 {
  padding-left: 1rem;
  padding-right: 1rem;
}

.px-6 {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

.py-1 {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.py-12 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.text-xs {
  font-size: 0.75rem;
}

.text-sm {
  font-size: 0.875rem;
}

.text-lg {
  font-size: 1.125rem;
}

.text-xl {
  font-size: 1.25rem;
}

.text-2xl {
  font-size: 1.5rem;
}

.text-3xl {
  font-size: 1.875rem;
}

.text-4xl {
  font-size: 2.25rem;
}

.font-bold {
  font-weight: 700;
}

.font-semibold {
  font-weight: 600;
}

.font-medium {
  font-weight: 500;
}

.text-gray-600 {
  color: #4b5563;
}

.text-gray-700 {
  color: #374151;
}

.text-gray-900 {
  color: #111827;
}

.text-white {
  color: #ffffff;
}

.text-green-600 {
  color: #16a34a;
}

.text-green-800 {
  color: #166534;
}

.text-blue-600 {
  color: #2563eb;
}

.text-purple-600 {
  color: #9333ea;
}

.text-purple-100 {
  color: #f3e8ff;
}

.text-yellow-800 {
  color: #92400e;
}

.bg-white {
  background-color: #ffffff;
}

.bg-gray-50 {
  background-color: #f9fafb;
}

.bg-gray-100 {
  background-color: #f3f4f6;
}

.bg-gray-200 {
  background-color: #e5e7eb;
}

.bg-green-100 {
  background-color: #dcfce7;
}

.bg-yellow-100 {
  background-color: #fef3c7;
}

.bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}

.from-purple-500 {
  --tw-gradient-from: #8b5cf6;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(139, 92, 246, 0));
}

.to-blue-500 {
  --tw-gradient-to: #3b82f6;
}

.from-purple-600 {
  --tw-gradient-from: #9333ea;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(147, 51, 234, 0));
}

.to-blue-600 {
  --tw-gradient-to: #2563eb;
}

.rounded-lg {
  border-radius: 0.5rem;
}

.rounded-xl {
  border-radius: 0.75rem;
}

.rounded-full {
  border-radius: 9999px;
}

.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.border {
  border-width: 1px;
}

.border-2 {
  border-width: 2px;
}

.border-4 {
  border-width: 4px;
}

.border-gray-200 {
  border-color: #e5e7eb;
}

.border-white {
  border-color: #ffffff;
}

.border-purple-500 {
  border-color: #8b5cf6;
}

.border-t-transparent {
  border-top-color: transparent;
}

.w-6 {
  width: 1.5rem;
}

.w-12 {
  width: 3rem;
}

.w-24 {
  width: 6rem;
}

.w-full {
  width: 100%;
}

.h-2 {
  height: 0.5rem;
}

.h-6 {
  height: 1.5rem;
}

.h-12 {
  height: 3rem;
}

.h-24 {
  height: 6rem;
}

.h-64 {
  height: 16rem;
}

.aspect-video {
  aspect-ratio: 16 / 9;
}

.object-cover {
  object-fit: cover;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.justify-center {
  justify-content: center;
}

.justify-between {
  justify-content: space-between;
}

.text-center {
  text-align: center;
}

.relative {
  position: relative;
}

.absolute {
  position: absolute;
}

.-bottom-1 {
  bottom: -0.25rem;
}

.-right-1 {
  right: -0.25rem;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}

.overflow-hidden {
  overflow: hidden;
}

.cursor-pointer {
  cursor: pointer;
}

.transition-colors {
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

.duration-300 {
  transition-duration: 300ms;
}

.hover\:bg-gray-200:hover {
  background-color: #e5e7eb;
}

.hover\:bg-gray-700:hover {
  background-color: #374151;
}

.hover\:bg-purple-700:hover {
  background-color: #7c3aed;
}

.hover\:bg-blue-700:hover {
  background-color: #1d4ed8;
}

.hover\:bg-green-700:hover {
  background-color: #15803d;
}

.hover\:text-gray-800:hover {
  color: #1f2937;
}

.hover\:text-purple-700:hover {
  color: #7c3aed;
}

.hover\:shadow-md:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Additional color classes */
.bg-pink-500 {
  background-color: #ec4899;
}

.bg-cyan-500 {
  background-color: #06b6d4;
}

.bg-yellow-400 {
  background-color: #facc15;
}

.text-yellow-400 {
  color: #facc15;
}

.bg-yellow-400\/20 {
  background-color: rgba(250, 204, 21, 0.2);
}

.bg-yellow-500\/10 {
  background-color: rgba(245, 158, 11, 0.1);
}

.bg-yellow-500\/20 {
  background-color: rgba(245, 158, 11, 0.2);
}

.border-yellow-500\/30 {
  border-color: rgba(245, 158, 11, 0.3);
}

.bg-orange-500\/10 {
  background-color: rgba(249, 115, 22, 0.1);
}

.bg-red-500\/10 {
  background-color: rgba(239, 68, 68, 0.1);
}

.bg-red-500\/20 {
  background-color: rgba(239, 68, 68, 0.2);
}

.border-red-500\/30 {
  border-color: rgba(239, 68, 68, 0.3);
}

.text-red-400 {
  color: #f87171;
}

.text-red-500 {
  color: #ef4444;
}

.bg-red-500 {
  background-color: #ef4444;
}

.bg-black\/70 {
  background-color: rgba(0, 0, 0, 0.7);
}

/* 视频播放相关样式 */
.video-modal {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.video-container {
  position: relative;
  overflow: hidden;
}

.video-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.1));
  pointer-events: none;
  z-index: 1;
}

/* 高光卡片悬停效果 */
.highlight-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.highlight-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 25px -5px rgba(139, 92, 246, 0.1), 0 10px 10px -5px rgba(139, 92, 246, 0.04);
}

/* 播放按钮动画 */
.play-button {
  transition: all 0.2s ease-in-out;
}

.play-button:hover {
  transform: scale(1.1);
  box-shadow: 0 0 20px rgba(139, 92, 246, 0.4);
}

/* 场次选择样式 */
.session-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.session-card:hover {
  transform: translateY(-1px);
}

.session-card.selected {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px -5px rgba(139, 92, 246, 0.2), 0 10px 10px -5px rgba(139, 92, 246, 0.04);
}

/* 金主排行榜样式 */
.reward-item {
  transition: all 0.2s ease-in-out;
}

.reward-item:hover {
  transform: translateX(4px);
}

/* 加载动画 */
.loading-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 防止横向滚动 */
.overflow-x-hidden {
  overflow-x: hidden;
}

/* Sentiment Reactions 卡片样式 */
.sentiment-card {
  min-height: 60px;
  transition: all 0.3s ease;
}

.sentiment-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px -8px rgba(139, 92, 246, 0.3);
}

/* 确保容器不会产生横向滚动 */
main {
  overflow-x: hidden;
  max-width: 100vw;
}

/* 响应式文本大小调整 */
@media (max-width: 768px) {
  .sentiment-card span {
    font-size: 0.75rem;
  }
}

/* 数字徽章样式优化 */
.sentiment-badge {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
